import React, { useCallback, useMemo } from 'react';

import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import SwitchFormField from '@components/form-fields/SwitchFormField';
import DeleteIcon from '@mui/icons-material/Clear';
import Error from '@mui/icons-material/Error';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { MAIN_THEME } from '@styles';

import { AmendmentPolicyCoverForm } from '../types/getAmendmentsDetailsResponse.types';

import { FormikErrors, FormikTouched } from 'formik';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '14px',
    position: 'relative'
  },
  row: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: '28.5% 22.5% 22.5% 22.5%'
  },
  rowTwoCols: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: '1fr 1fr'
  },
  rowSingleCol: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: '1fr'
  },
  removeBtn: {
    position: 'absolute',
    top: 6,
    right: 6
  },
  errorIconStyle: {
    height: '15px',
    width: '15px',
    color: 'red',
    position: 'absolute',
    top: '50%',
    right: '8px',
    transform: 'translateY(-50%)',
    cursor: 'pointer'
  }
}));

type Props = {
  row: AmendmentPolicyCoverForm;
  rowIndex: number;

  disabledForm: boolean;
  allowPlanCoverEdits: boolean;
  amendmentStatus: string;
  currencySymbol: string;

  policyEffectiveDate?: string;
  policyExpiryDate?: string;
  amendmentEffectiveDate?: string;

  selectOptions: Record<string, string>;
  disablePolicyCoverSelect: boolean;
  onRemove?: () => void;

  touched: FormikTouched<{ covers: AmendmentPolicyCoverForm[] }>;
  errors: FormikErrors<{ covers: AmendmentPolicyCoverForm[] }>;

  setFieldValue: (path: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched: (
    path: string,
    touched?: boolean,
    shouldValidate?: boolean
  ) => void;
  handleBlur: (fieldOrEvent: any) => void;
  onBlurPolicyCover: (selectedId?: string) => void;

  isLine51?: boolean;
  isCoverEffectiveToRequired?: boolean;
  isCancellation?: boolean;

  // Formik dirty state for coverPremiumVariance field
  isPremiumVarianceDirty?: boolean;

  // Property lines (2, 8, 23, 26): New Sum Insured / New Premium are derived
  // from the property risk cover distribution and are read-only.
  isPropertyLine?: boolean;
};

const YES_NO_OPTIONS = { Yes: 'Yes', No: 'No' } as const;

const PolicyCoverRowAtom: React.FC<Props> = ({
  row,
  rowIndex,

  disabledForm,
  allowPlanCoverEdits,
  amendmentStatus,
  currencySymbol,

  policyEffectiveDate,
  policyExpiryDate,
  amendmentEffectiveDate,

  selectOptions,
  disablePolicyCoverSelect,
  onRemove,

  touched,
  errors,

  setFieldValue,
  setFieldTouched,
  handleBlur,
  onBlurPolicyCover,
  isLine51,
  isCoverEffectiveToRequired,
  isCancellation,
  isPremiumVarianceDirty = false,
  isPropertyLine = false
}) => {
  const { classes } = useStyles();
  const base = `covers[${rowIndex}]`;

  // Dynamic row message function using Formik's dirty state
  const getDynamicRowMessage = useCallback((): string[] => {
    const messages: string[] = [];

    if (isCancellation && isPremiumVarianceDirty) {
      messages.push('Premium Variance has been changed.');
    }

    return messages;
  }, [isCancellation, isPremiumVarianceDirty]);

  const canToggleRemoved = useMemo(
    () => !row.isMandatory && !row.isNew && !isCancellation,
    [row.isMandatory, row.isNew, isCancellation]
  );

  const disabledRow = useMemo(() => {
    const isClosed = ['COMPLETED', 'CANCELLED', 'CANCELED'].includes(
      (amendmentStatus || '').toUpperCase()
    );

    // For cancellations, most fields should be read-only
    if (isCancellation) {
      return true;
    }

    return disabledForm || isClosed || !allowPlanCoverEdits || !row.isEditable;
  }, [
    disabledForm,
    amendmentStatus,
    allowPlanCoverEdits,
    row.isEditable,
    isCancellation
  ]);

  const disabledIsRemoved = useMemo(() => {
    const isClosed = ['COMPLETED', 'CANCELLED', 'CANCELED'].includes(
      (amendmentStatus || '').toUpperCase()
    );

    return disabledForm || isClosed || !allowPlanCoverEdits;
  }, [disabledForm, amendmentStatus, allowPlanCoverEdits]);

  const disabledPremiumVariance = useMemo(() => {
    const isClosed = ['COMPLETED', 'CANCELLED', 'CANCELED'].includes(
      (amendmentStatus || '').toUpperCase()
    );

    return disabledForm || isClosed || !allowPlanCoverEdits || !row.isEditable;
  }, [disabledForm, amendmentStatus, allowPlanCoverEdits, row.isEditable]);

  const showNewSection = !row.isNew;

  const handleRemovedToggle = useCallback(
    async (value: 'YES' | 'NO') => {
      const isRemoved = value === 'YES';
      await setFieldValue(`${base}.isRemoved`, isRemoved, false);
      await setFieldTouched(`${base}.isRemoved`, true, false);

      if (isRemoved && canToggleRemoved) {
        await setFieldValue(
          `${base}.coverEffectiveTo`,
          amendmentEffectiveDate,
          false
        );

        const calculateProRataForRemoved = () => {
          const d = (s?: string) => (s ? new Date(s).getTime() : NaN);
          const to = d(amendmentEffectiveDate);
          const from = d(row.coverEffectiveFrom);
          const pTo = d(policyExpiryDate);
          const pFrom = d(policyEffectiveDate);

          if (
            Number.isNaN(to) ||
            Number.isNaN(from) ||
            Number.isNaN(pTo) ||
            Number.isNaN(pFrom)
          )
            return 0;

          const top = Math.max(0, to - from);
          const bottom = Math.max(1, pTo - pFrom);
          const ratio = +(top / bottom);

          return ratio;
        };

        const pr = calculateProRataForRemoved();

        await setFieldValue(`${base}.proRataRatio`, pr, false);

        const round3 = (n?: number) =>
          typeof n === 'number' && !Number.isNaN(n) ? +n.toFixed(3) : undefined;

        const newPremiumAmountComputed =
          row.coverPremiumType === 'AMOUNT'
            ? row.coverPremiumAmount
              ? (row.coverPremiumAmount * pr).toFixed(3)
              : undefined
            : undefined;

        await setFieldValue(
          `${base}.newPremiumAmount`,
          newPremiumAmountComputed,
          false
        );

        await setFieldValue(
          `${base}.newPremiumPercentage`,
          row.coverPremiumType === 'PERCENTAGE'
            ? row.coverPremiumPercentage
            : undefined,
          false
        );
      } else if (!isRemoved && canToggleRemoved) {
        await setFieldValue(
          `${base}.coverEffectiveTo`,
          policyExpiryDate,
          false
        );

        await setFieldValue(`${base}.proRataRatio`, undefined, false);

        await setFieldValue(
          `${base}.newPremiumAmount`,
          row.coverPremiumType === 'AMOUNT'
            ? row.coverPremiumAmount
            : undefined,
          false
        );

        await setFieldValue(
          `${base}.newPremiumPercentage`,
          row.coverPremiumType === 'PERCENTAGE'
            ? row.coverPremiumPercentage
            : undefined,
          false
        );
      }
    },
    [
      base,
      setFieldValue,
      setFieldTouched,
      canToggleRemoved,
      amendmentEffectiveDate,
      policyExpiryDate,
      policyEffectiveDate,
      row.coverEffectiveFrom,
      row.coverPremiumType,
      row.coverPremiumAmount,
      row.coverPremiumPercentage,
      rowIndex
    ]
  );

  const err = (key: keyof AmendmentPolicyCoverForm) =>
    (touched?.covers?.[rowIndex] as any)?.[key]
      ? (errors?.covers?.[rowIndex] as any)?.[key]
      : '';

  const renderRowMessage = () => {
    const messages = getDynamicRowMessage();
    if (messages && messages.length > 0) {
      return (
        <Tooltip title={messages[0]}>
          <Error className={classes.errorIconStyle} />
        </Tooltip>
      );
    }

    return null;
  };

  return (
    <div className={classes.container}>
      {row.isNew && !row.isMandatory && onRemove && (
        <Tooltip title="Remove Policy Plan Cover">
          <IconButton
            size="small"
            className={classes.removeBtn}
            onClick={onRemove}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Section 1 / Row 1 - Hide isMain for cancellations */}
      <div className={classes.row}>
        <SelectFormField
          name={`${base}.policyCover`}
          title="Policy Cover*"
          placeholder="Policy Cover"
          value={row.policyCover}
          selectOptions={selectOptions}
          onBlur={(_, e) => {
            handleBlur(e);
            onBlurPolicyCover(row.policyCover);
          }}
          onChange={(v: any) => {
            const sel = String(v);
            setFieldValue(`${base}.policyCover`, sel, false);
            onBlurPolicyCover(sel);
          }}
          error={err('policyCover')}
          disabled={disablePolicyCoverSelect}
          material
        />

        {!isCancellation && (
          <SelectFormField
            name={`${base}.isMain`}
            title="Is Main*"
            placeholder="Is Main"
            value={row.isMain ? 'Yes' : 'No'}
            disabled
            selectOptions={YES_NO_OPTIONS}
            onBlur={() => handleBlur(`${base}.isMain`)}
            material
          />
        )}

        <SelectFormField
          name={`${base}.isMandatory`}
          title="Is Mandatory*"
          placeholder="Is Mandatory"
          value={row.isMandatory ? 'Yes' : 'No'}
          disabled
          selectOptions={YES_NO_OPTIONS}
          onBlur={() => handleBlur(`${base}.isMandatory`)}
          material
        />
      </div>

      <div className={classes.row}>
        <DatePickerFormField
          name={`${base}.coverEffectiveFrom`}
          title="Cover Effective From*"
          placeholder="Effective From"
          value={row.coverEffectiveFrom}
          error={err('coverEffectiveFrom')}
          onBlur={() => handleBlur(`${base}.coverEffectiveFrom`)}
          onDateChange={(v) =>
            setFieldValue(`${base}.coverEffectiveFrom`, v, false)
          }
          disabled
          material
        />
        <DatePickerFormField
          name={`${base}.coverEffectiveTo`}
          title={
            isCoverEffectiveToRequired
              ? 'Cover Effective To*'
              : 'Cover Effective To'
          }
          placeholder="Effective To"
          value={row.coverEffectiveTo}
          error={err('coverEffectiveTo')}
          onBlur={() => handleBlur(`${base}.coverEffectiveTo`)}
          onDateChange={(v) =>
            setFieldValue(`${base}.coverEffectiveTo`, v, false)
          }
          disabled
          material
        />
        <SelectFormField
          name={`${base}.sumInsuredIsAdditive`}
          title="Additive Sum Insured*"
          placeholder="Additive Sum Insured"
          value={row.sumInsuredIsAdditive ? 'Yes' : 'No'}
          disabled
          selectOptions={YES_NO_OPTIONS}
          onBlur={() => handleBlur(`${base}.sumInsuredIsAdditive`)}
          material
        />

        <CurrencyFormField
          name={`${base}.coverSumInsured`}
          title="Sum Insured*"
          placeholder="Sum Insured"
          value={row.coverSumInsured}
          error={err('coverSumInsured')}
          onBlur={(e) => handleBlur(e)}
          onChange={(e: any) =>
            setFieldValue(`${base}.coverSumInsured`, e.target.value, false)
          }
          disabled={disabledRow || !row.isNew}
          material
          currencySymbol={currencySymbol}
        />
      </div>

      <div className={classes.row}>
        {/* Show Premium Type only for line 51 or non-cancellation */}
        {(isLine51 || !isCancellation) && (
          <SelectFormField
            name={`${base}.coverPremiumType`}
            title="Premium Type*"
            placeholder="Premium Type"
            value={row.coverPremiumType}
            error={err('coverPremiumType')}
            onChange={(v: any) => {
              setFieldValue(`${base}.coverPremiumType`, v, false);
              setFieldValue(`${base}.coverPremiumAmount`, undefined, false);
              setFieldValue(`${base}.coverPremiumPercentage`, undefined, false);
            }}
            onBlur={(_, e) => handleBlur(e)}
            disabled={disabledRow || !row.isNew}
            selectOptions={{
              AMOUNT: 'Amount',
              PERCENTAGE: 'Percentage'
            }}
            material
          />
        )}

        {row.coverPremiumType === 'AMOUNT' && (isLine51 || !isCancellation) && (
          <CurrencyFormField
            name={`${base}.coverPremiumAmount`}
            title="Premium*"
            placeholder="Premium Amount"
            value={row.coverPremiumAmount ?? ''}
            error={err('coverPremiumAmount')}
            onBlur={(e) => handleBlur(e)}
            onChange={(e: any) =>
              setFieldValue(`${base}.coverPremiumAmount`, e.target.value, false)
            }
            disabled={disabledRow || !row.isNew}
            material
            currencySymbol={currencySymbol}
          />
        )}

        {row.coverPremiumType === 'PERCENTAGE' &&
          (isLine51 || !isCancellation) && (
            <PercentageFormField
              name={`${base}.coverPremiumPercentage`}
              title="Premium Percentage*"
              placeholder="Premium Percentage"
              value={row.coverPremiumPercentage}
              error={err('coverPremiumPercentage')}
              onBlur={(e) => handleBlur(e)}
              onChange={(e: any) =>
                setFieldValue(
                  `${base}.coverPremiumPercentage`,
                  e.target.value,
                  false
                )
              }
              disabled={disabledRow || !row.isNew}
              material
            />
          )}

        {/* Hide excess fields for cancellations */}
        {!isCancellation && (
          <SelectFormField
            name={`${base}.excessOnClaimType`}
            title="Excess Type*"
            placeholder="Excess On Claim Type"
            value={row.excessOnClaimType}
            error={err('excessOnClaimType')}
            onChange={(v: any) => {
              setFieldValue(`${base}.excessOnClaimType`, v, false);
              setFieldValue(`${base}.excessOnClaimAmount`, undefined, false);
              setFieldValue(
                `${base}.excessOnClaimPercentage`,
                undefined,
                false
              );
              setFieldValue(`${base}.excessOnClaimDays`, undefined, false);
            }}
            onBlur={(_, e) => handleBlur(e)}
            disabled={disabledRow || !row.isNew}
            selectOptions={{
              AMOUNT: 'Amount',
              PERCENTAGE: 'Percentage',
              DAYS: 'Days'
            }}
            material
          />
        )}

        {/* Hide excess amount/percentage/days fields for cancellations */}
        {row.excessOnClaimType === 'AMOUNT' && !isCancellation && (
          <CurrencyFormField
            name={`${base}.excessOnClaimAmount`}
            title="Excess*"
            placeholder="Excess On Claim"
            value={row.excessOnClaimAmount ?? ''}
            error={err('excessOnClaimAmount')}
            onBlur={(e) => handleBlur(e)}
            onChange={(e: any) =>
              setFieldValue(
                `${base}.excessOnClaimAmount`,
                e.target.value,
                false
              )
            }
            disabled={disabledRow || !row.isNew}
            material
            currencySymbol={currencySymbol}
          />
        )}

        {row.excessOnClaimType === 'PERCENTAGE' && !isCancellation && (
          <PercentageFormField
            name={`${base}.excessOnClaimPercentage`}
            title="Excess*"
            placeholder="Excess On Claim"
            value={row.excessOnClaimPercentage}
            error={err('excessOnClaimPercentage')}
            onBlur={(e) => handleBlur(e)}
            onChange={(e: any) =>
              setFieldValue(
                `${base}.excessOnClaimPercentage`,
                e.target.value,
                false
              )
            }
            disabled={disabledRow || !row.isNew}
            material
          />
        )}

        {row.excessOnClaimType === 'DAYS' && !isCancellation && (
          <NumberInputFormField
            name={`${base}.excessOnClaimDays`}
            title="Excess*"
            placeholder="Excess On Claim"
            value={row.excessOnClaimDays ?? ''}
            error={err('excessOnClaimDays')}
            onBlur={(e) => handleBlur(e)}
            onChange={(e: any) =>
              setFieldValue(`${base}.excessOnClaimDays`, e.target.value, false)
            }
            disabled={disabledRow || !row.isNew}
            material
            maxDecimalPrecision={0}
          />
        )}
      </div>

      {/* Hidden calculated fields for cancellation scenarios */}
      {isCancellation && (
        <>
          <input
            type="hidden"
            name={`${base}.coverPeriod`}
            value={(() => {
              const from = new Date(row.coverEffectiveFrom || '').getTime();
              const to = new Date(row.coverEffectiveTo || '').getTime();

              return !isNaN(from) && !isNaN(to)
                ? Math.ceil((to - from) / (1000 * 60 * 60 * 24))
                : 0;
            })()}
          />
          <input
            type="hidden"
            name={`${base}.cancelledPeriod`}
            value={(() => {
              const coverTo = new Date(row.coverEffectiveTo || '').getTime();
              const amendmentDate = new Date(
                amendmentEffectiveDate || ''
              ).getTime();

              return !isNaN(coverTo) && !isNaN(amendmentDate)
                ? Math.ceil((coverTo - amendmentDate) / (1000 * 60 * 60 * 24))
                : 0;
            })()}
          />
          <input
            type="hidden"
            name={`${base}.activeDays`}
            value={(() => {
              const amendmentDate = new Date(
                amendmentEffectiveDate || ''
              ).getTime();
              const coverFrom = new Date(
                row.coverEffectiveFrom || ''
              ).getTime();

              return !isNaN(amendmentDate) && !isNaN(coverFrom)
                ? Math.ceil((amendmentDate - coverFrom) / (1000 * 60 * 60 * 24))
                : 0;
            })()}
          />
        </>
      )}

      {/* Premium Variance field for cancellation scenarios */}
      {isCancellation && (
        <div className={classes.rowSingleCol} style={{ marginTop: 8 }}>
          <div style={{ position: 'relative' }}>
            <CurrencyFormField
              name={`${base}.coverPremiumVariance`}
              title="Premium Variance*"
              placeholder="Premium Variance"
              value={(row as any).coverPremiumVariance}
              error={err('coverPremiumVariance' as any)}
              onBlur={(e) => handleBlur(e)}
              onChange={(e) => {
                setFieldValue(
                  `${base}.coverPremiumVariance`,
                  e.target.value,
                  false
                );
              }}
              disabled={disabledPremiumVariance}
              material
              currencySymbol={currencySymbol}
              allowNegative={true}
              minValue={
                row.coverPremiumAmount ? -row.coverPremiumAmount : undefined
              }
              maxValue={0}
            />
            {renderRowMessage()}
          </div>
        </div>
      )}

      {/* Hide Is Removed toggle for cancellations */}
      {!row.isNew && !isCancellation && (
        <div className={classes.row} style={{ marginTop: 8 }}>
          <SwitchFormField
            name={`${base}.isRemoved`}
            title="Is Removed"
            value={!!row.isRemoved}
            onBlur={() => handleBlur(`${base}.isRemoved`)}
            onChange={(_, val: boolean) =>
              handleRemovedToggle(val ? 'YES' : 'NO')
            }
            disabled={disabledIsRemoved || row.isMandatory}
          />
        </div>
      )}

      {/* Hide entire New section for cancellations */}
      {showNewSection && !isCancellation && (
        <>
          <div className={classes.rowTwoCols} style={{ marginTop: 10 }}>
            <CurrencyFormField
              name={`${base}.newSumInsured`}
              title="New Sum Insured*"
              placeholder="New Sum Insured"
              value={row.newSumInsured}
              error={err('newSumInsured')}
              onBlur={(e) => handleBlur(e)}
              onChange={(e: any) =>
                setFieldValue(`${base}.newSumInsured`, e.target.value, false)
              }
              disabled={disabledRow || isPropertyLine}
              material
              currencySymbol={currencySymbol}
            />

            {isLine51 && row.coverPremiumType === 'PERCENTAGE' && (
              <PercentageFormField
                name={`${base}.newPremiumPercentage`}
                title="New Premium Percentage*"
                placeholder="New Premium Percentage"
                value={
                  row.newPremiumPercentage ?? row.coverPremiumPercentage ?? ''
                }
                error={err('newPremiumPercentage')}
                onBlur={(e) => handleBlur(e)}
                onChange={(e: any) =>
                  setFieldValue(
                    `${base}.newPremiumPercentage`,
                    e.target.value,
                    false
                  )
                }
                disabled={disabledRow}
                material
              />
            )}

            {row.coverPremiumType === 'AMOUNT' && (
              <CurrencyFormField
                name={`${base}.newPremiumAmount`}
                title="New Premium*"
                placeholder="New Premium"
                value={row.newPremiumAmount}
                error={err('newPremiumAmount')}
                onBlur={(e) => handleBlur(e)}
                onChange={(e: any) =>
                  setFieldValue(
                    `${base}.newPremiumAmount`,
                    e.target.value,
                    false
                  )
                }
                disabled={disabledRow || isPropertyLine}
                material
                currencySymbol={currencySymbol}
              />
            )}
          </div>

          <div className={classes.rowSingleCol} style={{ marginTop: 8 }}>
            {row.excessOnClaimType === 'DAYS' && (
              <NumberInputFormField
                name={`${base}.newExcessDays`}
                title="New Excess (Days)*"
                placeholder="New Excess (Days)"
                value={row.newExcessDays}
                error={err('newExcessDays')}
                onBlur={(e) => handleBlur(e)}
                onChange={(e: any) =>
                  setFieldValue(`${base}.newExcessDays`, e.target.value, false)
                }
                disabled={disabledRow}
                material
                maxDecimalPrecision={0}
              />
            )}

            {row.excessOnClaimType === 'AMOUNT' && (
              <CurrencyFormField
                name={`${base}.newExcessAmount`}
                title="New Excess (Amount)*"
                placeholder="New Excess (Amount)"
                value={row.newExcessAmount}
                error={err('newExcessAmount')}
                onBlur={(e) => handleBlur(e)}
                onChange={(e: any) =>
                  setFieldValue(
                    `${base}.newExcessAmount`,
                    e.target.value,
                    false
                  )
                }
                disabled={disabledRow}
                material
                currencySymbol={currencySymbol}
              />
            )}

            {row.excessOnClaimType === 'PERCENTAGE' && (
              <PercentageFormField
                name={`${base}.newExcessPercentage`}
                title="New Excess (Percentage)*"
                placeholder="New Excess (Percentage)"
                value={row.newExcessPercentage}
                error={err('newExcessPercentage')}
                onBlur={(e) => handleBlur(e)}
                onChange={(e: any) =>
                  setFieldValue(
                    `${base}.newExcessPercentage`,
                    e.target.value,
                    false
                  )
                }
                disabled={disabledRow}
                material
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export { PolicyCoverRowAtom };
